import Schema from 'validate';
import { type SchemaDefinition } from 'validate';
import { traDataValidator } from './tra-data-validator';
import { baseSchema } from './schema';
import { Attck } from './context/attck/repository';
import { AttckCommands } from './context/attck/commands';
import { type AttckData, type AttckJSON } from './context/attck/dto';
import { type Phases } from './context/phase/repositoty';
import { PhasesCommands } from './context/phase/commands';
import { type Phase } from './context/phase/entity';
import { PhaseValidator } from './phase-validator';
import { type PhaseLockRules } from './phase-lock-rules';

export interface TraBaseConfig {
    version: number;
    phases: Phase[];
    phaseLockRules: PhaseLockRules;
    schema: SchemaDefinition;
    attck?: AttckJSON;
}

export class TraBase {
    version: number;
    phases: Phases;
    phaseLockRules: PhaseLockRules;
    currentPhase?: Phase;
    schema: SchemaDefinition;
    attck: Attck;
    phaseValidator: PhaseValidator<this>;

    constructor(
        config: TraBaseConfig,
        protected changeCallback: () => void,
    ) {
        this.version = config.version;
        this.phaseValidator = new PhaseValidator({}, this);
        this.schema = config.schema;
        this.phases = new PhasesCommands().fromJSON(
            config.phases,
            this.changeCallback,
        );
        this.phaseLockRules = config.phaseLockRules;
        this.attck = new Attck(this.changeCallback);
    }

    protected _error: string | null = null;

    get error(): string | null {
        return this._error;
    }

    setAttckData(data: AttckData): void {
        new AttckCommands().set(this.attck, data);
    }

    parse(data: Record<string, unknown>): void {
        this.validateBase(data);
        if (this.error) {
            return;
        }
        this.validate(data);
        if (this.error) {
            return;
        }
        const validData = data as unknown as TraBaseConfig;
        this._error = null;
        this.phases = new PhasesCommands().fromJSON(
            validData.phases,
            this.changeCallback,
        );
        this.schema = validData.schema;
        if (validData.attck) {
            this.attck = new AttckCommands().fromJSON(
                validData.attck,
                this.changeCallback,
            );
        }
    }

    toJSON(): object {
        return {
            version: this.version,
            schema: this.schema,
            phases: this.phases.toJSON(),
            currentPhase: this.currentPhase?.toJSON(),
            attck: this.attck.toJSON(),
        };
    }

    toString(): string {
        return JSON.stringify(this.toJSON());
    }

    updateData<T>(data: Partial<T>): void {
        Object.assign(this, data);
    }

    protected validate(data: Record<string, unknown>): void {
        this._error = traDataValidator(data, new Schema(this.schema));
        if (this._error) {
            // eslint-disable-next-line no-console -- this package has no logging dependency; surface validation failures for consumers debugging invalid TRA documents
            console.warn(this._error);
        }
    }

    private validateBase(data: Record<string, unknown>): void {
        if (data.version !== this.version) {
            this._error = 'Unsupported version';
            return;
        }
        this._error = traDataValidator(data, new Schema(baseSchema));
        if (this._error) {
            // eslint-disable-next-line no-console -- this package has no logging dependency; surface validation failures for consumers debugging invalid TRA documents
            console.warn(this._error);
        }
    }
}
